import React, { useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import ImageGeneratorTracker from "./ImageGeneratorTracker";
import CollectionBuildTracker from "./CollectionBuildTracker";
import { pageShellClassName } from "./collectionBuildConstants";
import { useImageGeneratorJobs } from "./ImageGeneratorJobsContext";
import {
  DEFAULT_GENERATIVE_PLATFORM_LIBRARY_MODE,
  GENERATIVE_PLATFORM_LIBRARY_COLLECTION_BUILD_INTRO,
  GENERATIVE_PLATFORM_LIBRARY_IMAGE_GENERATOR_INTRO,
  GENERATIVE_PLATFORM_LIBRARY_MODE_KEY,
  GENERATIVE_PLATFORM_LIBRARY_MODE_LABEL,
  GenerativePlatformLibraryMode,
  isGenerativePlatformLibraryMode,
} from "./generativePlatformLibraryConstants";

type Props = {
  isSidebarOpen: boolean;
};

type LibraryLocationState = {
  librarySource?: GenerativePlatformLibraryMode;
};

const readStoredMode = (): GenerativePlatformLibraryMode | null => {
  try {
    const stored = localStorage.getItem(GENERATIVE_PLATFORM_LIBRARY_MODE_KEY);
    return isGenerativePlatformLibraryMode(stored) ? stored : null;
  } catch {
    return null;
  }
};

const resolveInitialMode = (
  locationState: LibraryLocationState | null
): GenerativePlatformLibraryMode => {
  const fromNav = locationState?.librarySource;
  if (fromNav && isGenerativePlatformLibraryMode(fromNav)) {
    return fromNav;
  }
  return readStoredMode() ?? DEFAULT_GENERATIVE_PLATFORM_LIBRARY_MODE;
};

const GenerativePlatformLibrary: React.FC<Props> = ({ isSidebarOpen }) => {
  const location = useLocation();
  const { openSession: openImageSession } = useImageGeneratorJobs();
  const [mode, setMode] = useState<GenerativePlatformLibraryMode>(() =>
    resolveInitialMode((location.state as LibraryLocationState | null) ?? null)
  );

  const selectMode = useCallback((next: GenerativePlatformLibraryMode) => {
    setMode(next);
    try {
      localStorage.setItem(GENERATIVE_PLATFORM_LIBRARY_MODE_KEY, next);
    } catch {
      // ignore quota / private mode
    }
  }, []);

  const widthClass = !isSidebarOpen
    ? "w-[calc(100vw-265px)]"
    : "w-[calc(100vw-105px)]";

  return (
    <div className={`${widthClass} ${pageShellClassName}`}>
      <div className="w-full pb-16">
        <div className="mb-5 flex items-center text-xl sm:text-[22px]">
          <button
            type="button"
            onClick={() => selectMode("image-generator")}
            className={`pr-3 font-semibold ${
              mode === "image-generator" ? "text-secondary" : "text-tertiary"
            }`}
          >
            {GENERATIVE_PLATFORM_LIBRARY_MODE_LABEL["image-generator"]}
          </button>
          <span className="font-semibold text-secondary">|</span>
          <button
            type="button"
            onClick={() => selectMode("collection-build")}
            className={`px-3 font-semibold ${
              mode === "collection-build" ? "text-secondary" : "text-tertiary"
            }`}
          >
            {GENERATIVE_PLATFORM_LIBRARY_MODE_LABEL["collection-build"]}
          </button>
        </div>

        {mode === "image-generator" ? (
          <>
            <p className="mb-5 text-sm text-secondary">
              {GENERATIVE_PLATFORM_LIBRARY_IMAGE_GENERATOR_INTRO}
            </p>
            <ImageGeneratorTracker onOpen={openImageSession} />
          </>
        ) : (
          <>
            <p className="mb-5 text-sm text-secondary">
              {GENERATIVE_PLATFORM_LIBRARY_COLLECTION_BUILD_INTRO}
            </p>
            <CollectionBuildTracker />
          </>
        )}
      </div>
    </div>
  );
};

export default GenerativePlatformLibrary;
